import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { ScrollText, Loader2, AlertTriangle, Calendar, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

const Logs = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      // ⚠️ ข้อมูล Log จะถูก Backend กรองให้แสดงเฉพาะของโรงเรียนนี้อัตโนมัติ
      const data = await api.getLogs(selectedDate);
      setLogs(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'ไม่สามารถดึงข้อมูล Log ได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [selectedDate]);

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp.replace(' ', 'T'));
      return format(date, 'd MMM yyyy, HH:mm:ss', { locale: th });
    } catch (e) {
      return timestamp;
    }
  };

  const renderDetails = (details) => {
    if (!details) return 'N/A';
    try {
      const parsed = JSON.parse(details);
      return (
        <ul className="list-disc list-inside text-xs">
          {Object.entries(parsed).map(([key, value]) => (
            <li key={key}>
              <strong className="capitalize">{key}:</strong> {String(value)}
            </li>
          ))}
        </ul>
      );
    } catch (e) {
      return <span className="text-xs">{details}</span>;
    }
  };

  // Helper เล็กๆ ไว้แสดงวันที่ไทยในข้อความว่างเปล่า
  const formatDateThai = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMM yyyy', { locale: th });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow p-6 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <ScrollText className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              ประวัติการใช้งาน (Logs)
            </h1>
            <p className="text-gray-600">
              ตรวจสอบกิจกรรมที่เกิดขึ้นในระบบรายวัน
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button 
            onClick={fetchLogs}
            className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 text-gray-600 transition-colors"
            title="รีเฟรชข้อมูล"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading && (
          <div className="p-12 flex flex-col items-center justify-center text-gray-500">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
            <p className="mt-4 text-lg">กำลังโหลดข้อมูล...</p>
          </div>
        )}

        {error && (
          <div className="p-12 flex flex-col items-center justify-center text-red-600 bg-red-50 border-y border-red-200">
            <AlertTriangle className="h-12 w-12" />
            <p className="mt-4 text-lg font-semibold">เกิดข้อผิดพลาด</p>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    เวลา
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ผู้ใช้งาน
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    การกระทำ (Action)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    รายละเอียด
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      ไม่พบประวัติการใช้งานในวันที่ {formatDateThai(selectedDate)}
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.log_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {log.employee_name || (log.employee_id ? `(${log.employee_id})` : 'N/A')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                        {log.action}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs break-words">
                        {renderDetails(log.details)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        {log.ip_address}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      <div className="text-right text-xs text-gray-500 font-medium px-2">
        แสดงข้อมูลวันที่: {formatDateThai(selectedDate)} (พบทั้งหมด {logs.length} รายการ)
      </div>
    </div>
  );
};

export default Logs;